import { useCallback, useState, useRef } from 'react';
import PrintIcon from '@repo/ui/src/components/Icons/PrintIcon';
import ShareIcon from '@repo/ui/src/components/Icons/ShareIcon';
import Button, { ButtonVariant } from '@repo/ui/src/components/Button';

interface PlanActionsProps {
  onPrint: () => void;
  shareLabel?: string;
  linkCopiedLabel?: string;
  printLabel?: string;
  theme: 'dark' | 'light';
}

export function PlanActions({ onPrint, printLabel, shareLabel, linkCopiedLabel, theme }: PlanActionsProps) {
  const isDark = theme === 'dark';
  const [tooltipVisible, setTooltipVisible] = useState(false);
  const tooltipTimer = useRef<number>();

  const onShare = useCallback(() => {
    navigator.clipboard.writeText(window.location.href).then(() => {
      setTooltipVisible(true);
      window.clearTimeout(tooltipTimer.current);
      tooltipTimer.current = window.setTimeout(() => setTooltipVisible(false), 2000);
    });
  }, []);

  return (
    <div className="flex gap-4 mt-10 print:hidden">
      <div className="relative flex-1">
        <Button
          type="button"
          variant={ButtonVariant.secondary}
          negative={isDark}
          full
          onClick={onShare}
          className="flex flex-row items-center justify-center px-2"
        >
          <ShareIcon className="inline-block w-10 h-10 mr-2 align-middle" />
          <span>
            {shareLabel ?? 'Share'}
          </span>
        </Button>
        {tooltipVisible && (
          <span
            role="tooltip"
            className="absolute z-10 px-3 py-2 mb-2 text-sm text-white -translate-x-1/2 rounded shadow-lg whitespace-nowrap bottom-full left-1/2 bg-neutral-600"
          >
            {linkCopiedLabel ?? 'Link Copied'}
            <span className="absolute -translate-x-1/2 border-4 border-transparent left-1/2 top-full border-t-neutral-600" />
          </span>
        )}
      </div>
      <div className="flex-1">
        <Button
          type="button"
          variant={ButtonVariant.secondary}
          negative={isDark}
          full
          onClick={onPrint}
          className="flex flex-row items-center justify-center px-2"
        >
          <PrintIcon className="inline-block w-10 h-10 mr-2 align-middle" />
          <span>
            {printLabel ?? 'Print'}
          </span>
        </Button>
      </div>
    </div>
  );
}
